import {
  faFileInvoice,
  faFilePdf,
  faIndustry,
  faMoneyCheckDollar
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type ReactElement, useState } from 'react'
import { createPortal } from 'react-dom'
import z from 'zod'
import { useCompany } from '~/api/queries/company'
import { useOrder } from '~/api/queries/order'
import {
  DateInput,
  Dropdown,
  Form,
  InputCol,
  InputLine,
  Submit,
  TextareaInput,
  TextInput
} from '~/components/Form'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import type { StandardModalProps } from '~/components/Global/Design/Modal/StandardModal'
import { StandardModal } from '~/components/Global/Design/Modal/StandardModal'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { useConfig } from '~/domains/configuration/queries/config'
import { ContactDropdownOption } from '~/domains/contacts/components/ContactDropdownOption'
import { fetchInvoicePdfBlob } from '~/domains/finance/utils/fetchInvoicePdfBlob'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { useSafeNavigateToLinkFromLegacy } from '~/hooks/useSafeNavigateToLinkFromLegacy'
import { toast } from '~/utils'
import { formatName } from '~/utils/names'
import { useCreateInvoiceMutation } from '../mutations/create'
import { useUpdateInvoiceMutation } from '../mutations/update'
import { useInvoice } from '../queries/invoice'
import { InvoiceStatus, InvoiceStatusLabels } from '../types'
import { findDefaultInvoiceContact } from '../utils/defaultInvoiceContact'
import { computeInvoiceTotals } from '../utils/invoiceTotals'
import { buildCreatePayload, buildUpdatePayload } from '../utils/payloads'
import { statusColors } from './InvoiceCard.css'
import { InvoiceLinesFieldArray } from './InvoiceLinesFieldArray'
import { InvoiceTotals } from './InvoiceTotals'

const invoiceLineSchema = z.object({
  description: z.string().min(1, 'La description est requise'),
  quantity: z.coerce
    .number()
    .int('La quantité doit être un entier')
    .refine((quantity) => quantity !== 0, 'La quantité ne peut pas être nulle'),
  unitPriceHT: z.coerce.number(),
  vatRateId: z.string().min(1, 'Le taux de TVA est requis')
})

const invoiceSchema = z.object({
  label: z.string().min(1, 'Le libellé est requis'),
  status: z.string().min(1, 'Le statut est requis'),
  order: z.string().nullish(),
  contact: z.string().nullish(),
  type: z.string().min(1, 'Le type de facture est requis'),
  issuedDate: z.date(),
  dueDate: z.date().nullish(),
  paymentDate: z.date().nullish(),
  externalRef: z.string().nullish(),
  paymentMethod: z.string().nullish(),
  paidAmount: z.coerce.number().nullish(),
  comments: z.string().nullish(),
  lines: z.array(invoiceLineSchema).min(1, 'Au moins une ligne est requise')
})

type FormValues = z.infer<typeof invoiceSchema>

type InvoiceModalProps = Omit<
  StandardModalProps,
  'children' | 'buttons' | 'icon' | 'title'
> & {
  invoiceId?: number
  orderId?: number
  contactId?: number
}

export const InvoiceModal = ({
  open,
  onClose,
  invoiceId,
  orderId
}: InvoiceModalProps): ReactElement => {
  const navigateToLinkFromLegacy = useSafeNavigateToLinkFromLegacy()
  const pathname = window.location.pathname
  const [createdInvoiceId, setCreatedInvoiceId] = useState<number | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  const isEditMode = !!(invoiceId ?? createdInvoiceId)

  const invoiceQueryId = invoiceId ?? createdInvoiceId ?? 0
  const { data: invoice, isLoading: isLoadingInvoice } =
    useInvoice(invoiceQueryId)

  const { data: order, isLoading: isLoadingOrder } = useOrder(
    orderId ?? invoice?.order.id ?? 0
  )

  const { data: companyData, isLoading: isLoadingCompany } = useCompany(
    invoice?.order.company.id ?? order?.company.id
  )

  const isLoadingContactList = isLoadingCompany || isLoadingOrder

  const companyContacts = (companyData?.contacts ?? []).filter(
    (c) => !c.archived
  )
  const defaultContactId = findDefaultInvoiceContact(
    companyContacts,
    order?.contact?.id
  )?.id

  const createInvoiceMutation = useCreateInvoiceMutation()
  const updateInvoiceMutation = useUpdateInvoiceMutation()
  const { data: invoiceTypes = [], isLoading: isLoadingTypes } =
    useTaxonomy('invoice_types')
  const { data: vatRates = [], isLoading: isLoadingVatRates } =
    useTaxonomy('vat_rates')
  const { data: invoicePaymentMethodDefault } = useConfig(
    'finance.invoice_payment_method_default'
  )
  const { data: legacyUrlBase } = useConfig('organization.legacy_url')

  const invoiceSchemaWithVat = invoiceSchema.superRefine((values, ctx) => {
    const { totalTTC } = computeInvoiceTotals(
      values.lines.map((line) => ({
        quantity: Number(line.quantity || 0),
        unitPriceHT: Number(line.unitPriceHT || 0),
        rate: Number(
          (vatRates.find((v) => String(v.id) === String(line.vatRateId)) as any)
            ?.rate || 0
        )
      }))
    )
    const paid = Number(values.paidAmount ?? 0)
    if (
      paid !== 0 &&
      (paid * totalTTC <= 0 || Math.abs(paid) > Math.abs(totalTTC))
    ) {
      ctx.addIssue({
        code: 'custom' as any,
        message:
          'Le montant réglé doit être du même signe que le total TTC et ne peut pas le dépasser.',
        path: ['paidAmount']
      })
    }
  })

  const handleSubmit = (data: FormValues) => {
    const toLocalDate = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

    const issuedAtString = data.issuedDate
      ? toLocalDate(data.issuedDate)
      : toLocalDate(new Date())
    const dueDateString = data.dueDate ? toLocalDate(data.dueDate) : null
    const paymentDateString = data.paymentDate
      ? toLocalDate(data.paymentDate)
      : null

    if (isEditMode && invoice) {
      const updatePayload = buildUpdatePayload(
        data,
        issuedAtString,
        dueDateString,
        paymentDateString,
        invoice
      )

      updateInvoiceMutation.mutate(
        { id: invoiceQueryId, data: updatePayload },
        {
          onSuccess: (updated) => {
            if (updated && typeof updated.id === 'number') {
              setCreatedInvoiceId(updated.id)
            }
          }
        }
      )
    } else {
      const payload = buildCreatePayload(
        data,
        issuedAtString,
        dueDateString,
        orderId
      )

      createInvoiceMutation.mutate(payload, {
        onSuccess: (created) => {
          if (created && typeof created.id === 'number') {
            setCreatedInvoiceId(created.id)
          }
        }
      })
    }
  }

  const handleValidateInvoice = () => {
    if (!isEditMode || !invoiceQueryId) return

    updateInvoiceMutation.mutate({
      id: invoiceQueryId,
      data: { status: InvoiceStatus.VALIDATED }
    })
  }

  const isPending = isEditMode
    ? updateInvoiceMutation.isPending
    : createInvoiceMutation.isPending

  const isEditable =
    (!isPending &&
      (!isEditMode ||
        (isEditMode &&
          invoice &&
          invoice.status !== InvoiceStatus.FULLY_PAID &&
          invoice.status !== InvoiceStatus.PARTIALLY_PAID))) ??
    false

  const footerButtons = [
    <>
      {invoice && invoice.status === InvoiceStatus.DRAFT && (
        <Button
          type="button"
          variation={ButtonVariation.Success}
          onClick={handleValidateInvoice}
          disabled={isPending}
        >
          Émettre la facture
        </Button>
      )}
      <Submit
        isSubmitting={false}
        isSubmitSuccessful={false}
        loadingText="Enregistrement..."
        variation={ButtonVariation.Primary}
        disabled={
          isPending || isLoadingTypes || isLoadingInvoice || isLoadingVatRates
        }
      >
        {isPending
          ? 'Enregistrement...'
          : isEditMode
            ? 'Enregistrer'
            : 'Créer une facture'}
      </Submit>
    </>
  ]

  if (isEditMode ? isLoadingInvoice || !invoice : isLoadingContactList) {
    return createPortal(
      <StandardModal
        open={open}
        onClose={onClose}
        title="Chargement..."
        icon={faMoneyCheckDollar}
      >
        <Loader />
      </StandardModal>,
      document.querySelector('#appWrapper') as HTMLElement
    )
  }

  const initialLines =
    isEditMode && invoice?.lines
      ? invoice.lines.map((line: any) => ({
          description: line.description,
          quantity: line.quantity,
          unitPriceHT: Number.parseFloat(line.unitPriceHT),
          vatRateId: String(line.vatRate?.id ?? '')
        }))
      : [
          {
            description: '',
            quantity: 1,
            unitPriceHT: 0,
            vatRateId: vatRates.length > 0 ? String(vatRates[0]?.id) : ''
          }
        ]

  const downloadPdf = async () => {
    if (!invoice) {
      return
    }
    setPdfLoading(true)
    try {
      const { blob, filename } = await fetchInvoicePdfBlob(invoice.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename ?? `facture-${String(invoice.id)}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast('Erreur', 'Impossible de générer le PDF.', ToastType.Danger)
    } finally {
      setPdfLoading(false)
    }
  }

  const headerButtons = invoice
    ? [
        <ButtonGroup align="right" key={'headerButtons'}>
          <Tooltip text="Télécharger le PDF" asChild direction="top">
            <Button
              variation={ButtonVariation.Neutral}
              buttonSize="small"
              type="button"
              disabled={pdfLoading}
              onClick={() => void downloadPdf()}
              aria-label="Télécharger le PDF"
            >
              <FontAwesomeIcon icon={faFilePdf} />
            </Button>
          </Tooltip>
          <Tooltip text="Voir la commande" asChild direction="top">
            <Button
              variation={ButtonVariation.Neutral}
              buttonSize="small"
              type="button"
              onClick={() =>
                pathname === `/orders/${invoice.order.id}`
                  ? onClose()
                  : navigateToLinkFromLegacy(`/orders/${invoice.order.id}`)
              }
            >
              <FontAwesomeIcon icon={faFileInvoice} /> #{invoice.order.id}
            </Button>
          </Tooltip>
          <Tooltip text="Voir l'entreprise" asChild direction="top">
            <Button
              variation={ButtonVariation.Neutral}
              buttonSize="small"
              type="button"
              disabled={!legacyUrlBase}
              onClick={() => {
                if (legacyUrlBase) {
                  window.open(
                    `${legacyUrlBase}/admin/entreprises/edit/${invoice.order.company.id}`,
                    '_blank'
                  )
                }
              }}
            >
              <FontAwesomeIcon icon={faIndustry} />
            </Button>
          </Tooltip>
        </ButtonGroup>
      ]
    : []

  const modalTitle = isEditMode
    ? `Facture ${invoice?.id || ''} - ${invoice?.label || ''} `
    : 'Créer la facture'

  const subtitle =
    isEditMode && invoice ? (
      <Badge
        title={InvoiceStatusLabels[invoice.status]}
        backgroundColor={statusColors[invoice.status]}
      />
    ) : undefined

  return createPortal(
    <Form
      key={isEditMode ? invoiceQueryId : 'new'}
      schema={invoiceSchemaWithVat}
      defaultValues={{
        label: isEditMode && invoice ? invoice.label : '',
        status: isEditMode && invoice ? invoice.status : InvoiceStatus.DRAFT,
        order: isEditMode ? undefined : String(orderId),
        contact:
          isEditMode && invoice
            ? String(invoice.contact?.id || '')
            : String(defaultContactId ?? ''),
        type: isEditMode && invoice ? String(invoice.type.id) : '',
        issuedDate:
          isEditMode && invoice
            ? new Date(
                `${(invoice.issuedAt ?? invoice.createdAt).slice(0, 10)}T00:00:00`
              )
            : new Date(),
        dueDate:
          isEditMode && invoice?.dueDate
            ? new Date(`${invoice.dueDate.slice(0, 10)}T00:00:00`)
            : null,
        paymentDate:
          isEditMode && invoice?.paymentDate
            ? new Date(`${invoice.paymentDate.slice(0, 10)}T00:00:00`)
            : null,
        externalRef:
          isEditMode && invoice ? (invoice.externalRef ?? null) : null,
        paymentMethod:
          isEditMode && invoice
            ? (invoice.paymentMethod ?? null)
            : (invoicePaymentMethodDefault ?? null),
        paidAmount: isEditMode && invoice ? invoice.paidAmount : 0,
        comments: isEditMode && invoice ? invoice.comments : null,
        lines: initialLines
      }}
      onSubmit={handleSubmit}
    >
      <StandardModal
        open={open}
        onClose={onClose}
        title={modalTitle}
        subtitle={subtitle}
        headerButtons={headerButtons}
        icon={faMoneyCheckDollar}
        buttons={footerButtons}
      >
        <InputCol>
          <InputLine>
            <TextInput
              name="label"
              label="Libellé de la facture"
              placeholder="Libellé de la facture"
              disabled={!isEditable}
              required
            />
            <DateInput
              name="issuedDate"
              label="Date d'émission"
              fullWidth={false}
              disabled={isPending}
              clearable={false}
            />
          </InputLine>
          <InputLine>
            <Dropdown
              name="type"
              label="Type de facture"
              placeholder="Sélectionner un type"
              options={invoiceTypes.map((type) => ({
                label: type.name,
                value: String(type.id)
              }))}
              optionsLoading={isLoadingTypes}
              required
              disabled={!isEditable}
            />
            <Dropdown
              name="contact"
              label="Contact"
              placeholder="Sélectionner un contact"
              disabled={!isEditable}
              optionsLoading={isLoadingContactList}
              options={companyContacts.map((contact) => ({
                label: formatName(contact),
                value: String(contact.id),
                element: <ContactDropdownOption contact={contact} />
              }))}
            />
          </InputLine>

          <InputLine>
            <DateInput
              name="dueDate"
              label="Date d'échéance"
              fullWidth={false}
              disabled={!isEditable}
            />
            <TextInput
              name="externalRef"
              label="Réf. externe"
              placeholder="N° de bon de commande…"
              disabled={!isEditable}
            />
            <TextInput
              name="paymentMethod"
              label="Mode de règlement"
              placeholder={invoicePaymentMethodDefault ?? ''}
              disabled={!isEditable}
            />
          </InputLine>
          {invoice && (
            <InputLine>
              <TextInput
                name="paidAmount"
                label="Montant réglé"
                type="number"
                step="0.01"
                placeholder="0.00"
                disabled={isPending}
              />
              <DateInput
                name="paymentDate"
                label="Date de règlement"
                fullWidth={false}
                disabled={isPending}
              />
            </InputLine>
          )}

          <InvoiceLinesFieldArray
            name="invoice-lines"
            isEditable={isEditable}
          />

          <InvoiceTotals
            name="invoice-totals"
            isEditMode={isEditMode}
            invoice={invoice}
          />

          <TextareaInput
            name="comments"
            label="Commentaire client"
            placeholder="Visible en bas de la facture PDF"
            disabled={!isEditable}
            rows={4}
          />
        </InputCol>
      </StandardModal>
    </Form>,
    document.querySelector('#appWrapper') as HTMLElement
  )
}
